import { ChainType } from "@/constants/chain-specific";
import type { RequiredAggregatorChainType, TokenIndex } from "@/constants/project-specific";
import { SHIB_TOKENS, DOGE_TOKENS, tokenIndexes } from "@/constants/project-specific/tokens";
import { type TokenBalance, BlockscoutApi } from "@/lib/blockscout-api";
import type { Address } from "viem";

interface NftTypeObject {
  erc721: TokenBalance[];
  erc1155: TokenBalance[];
}

interface UserBalances {
  tokens: TokenBalance[];
  nfts: NftTypeObject;
}

export const findTokenByAddress = (
  address: Address, chainId?: ChainType
): TokenIndex[keyof TokenIndex] | undefined => {
  const tokenIndex = tokenIndexes[chainId as RequiredAggregatorChainType] ?? SHIB_TOKENS;
  return Object.values(tokenIndex).find(
    (token) => token.address.toLowerCase() === address.toLowerCase()
  );
};

export const createRegexFromWildcard = (pattern: string) => {
  const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape special characters
  const regexPattern = escapedPattern.replace(/\\\*/g, ".*"); // Replace '*' with '.*' for wildcard matching
  return new RegExp(`^${regexPattern}$`, "i"); // Case-insensitive matching
};

export const blacklistedTokens = {
  names: [
    "ChewyLP",
    "TEST",
    "Dividen_Tracker",
    "INVESTToken_Dividen_Tracker",
    "Deployer Test",
    "*test*",
    "Yodedex LP",
    "Kibble LP",
    "DogeShrek Liquidity Token",
    "Wojak LPs",
    "*stbot.xyz*",
    "Win:*",
    "*Win:*",
    "Thank you for*",
    "*AUTOADD*",
    "*USD FOR FREE*",
    "*HYPE Drop*",
    "*Diamond Club*",
    "*! (*",
    "*! [*",
    "*Box #697*",
    "*ERC1155*",
    "*Cubes 86*",

  ],
  symbols: [
    "ChewyLP",
    "DST-V2",
    "UNI-V2",
    "Kibble-LP",
    "TEST",
    "T_Dividend_Tracker",
    "BULL_Dividend_Tracker",
    "SSLP",
    "DIVIDEND_TRACKER",
    "*TEST*",
    "*Test*",
    "SHIB-V2-POS",
    "INVESTToken_Dividen_Tracker",
    "dogeshrek-lp",
    "Wojk-LP",
    "*Claim*",
    "*Airdrop*",
    "Collect On:*",
    "Visit*",
    "vAMM-*",
    "SUI-NFT",

  ],
  addresses: [
    "0xe13824Fb7b206E585c775B30431600528572C3E7", // sKNINE (Staked token)
    "0x5592965B6F83B2Ea8957dEDCddbB2c24495e0F81",
    "0xfe668A3d6f05e7799AAE04659fC274AC00d094c0",
    "0x3D14CFF84600Be638c79CD2992Db5D9d85cE8994",
    "0x66880B6c2f9deB9Cf9e996f6126322219B1B36De",
    "0x87Ad4405D48FB5c7140475f3DbBC049357DA6961",
    "0x1fA31c850E68dAb3950b728BF1D7a43248dd1283",
    "0xc8Bb134c9592eBAF129c16D09bB71CA7C717c204",
    "0x567E4aB1041Cf2E2A15D4Ee7c9b9000Af7844Ae7", // ShibAI (Rugged)
    "0x8Fbc5bFcA9a9368E829E35e0e2688a44c3F2543a",
    "0xCDbe27aCb4579b1E2ef2F4Efc4c4a6f7E63933b2", // Brick By Brick (Deprecated)
    "0x73322C3ab6874d1f53561273bD5DFCb2d9e7b1d0", // MemeSwap (RUG)
    "0x73322C3ab6874d1f53561273bD5DFCb2d9e7b1d0", // MemeSwap (RUG)
    "0xd348D4f38f9f6710ae18580B161024D3C115F512", // Boneshi (GONE)
    "0xE5980c4d4A071B55a804dc9D2Dc9b5102977c07C", // MemeSwap (RUG)
    "0xd1509c2Aa2411b993dE76d65523F8Af96F86c943", // SPUNKZ (Migrated)
    "0xab82A674dF5F2F023d5Aac2e5706aEC596A9f3D0", // Boneee
    "0x1c1Ba7647374d13700b670559db4761CD6D83fE7", // Boneee
    "0xBAB4242970A2998Aec94d40D541F0d30EE074Bb3", // Boneee
    "0x83e11ce585d9272164c2215DdE9d029f0F441f60", // Aeon AI (Deprecated)
    "0xf9Ce63CF23F49e0Ee26Ad681eB86f430ca8DA6Df", // TechRefresh (RUG)
    "0x3a71e24Aec6F1004780450dB9Bfaf6DCB72965fB", // Sharbi (Deprecated)
    "0xA2f74e7f98A42CB56686376048187a660c78AaB0", // TYSON (Deprecated)
    "0x9D3E821ff67Cc010313E6CDBA159d5b6305e9431", // EWW (RUG)
    "0x1DE82708B1DB100e60518b7754725f19D72b9dde", // ShiBoy (RUG)
    "0xDEb93f58eA48681CC868a3078991b2371477F9B5", // Shibaswap (RUG)
    "0x871FBcAaeAfCf7aD2E4297E8819518b5C751A872", // TOONS (RUG)
    "0x8Ed45e03Bd5616d99c89901ebC8Fb51fF68b7d02", // SSHIBARIUM (RUG)
    "0x51079262861Adc9019Ad9C43D7273A6858DDd52A", // CZ (RUG)
    "0x43AB6e79a0ee99e6cF4eF9e70b4C0c2DF5A4d0Fb", // CryptiQ (Migrated)
    "0xd33988452D0ECA594B8Ab931CF668ea036Cb0c94", // BoneTech (RUG)
    "0x1E9040Be68A89182db7Aeb32D2547A1D1b92B846", // BuffShiba (RUG)
    "0x1E7C803B1094d0a7715ab933169e1ca31B4213a9", // JAM (RUG)
    "0x0D93829419f5cfD0245602941Cf68603f8F154C9", // Honey (RUG)
    "0x832Ba199a21d02104CAea01440aaB52C00F9074a", // BACCA (RUG)
    "0x9321a88707B20E6Eb2EBcF1EFCD2Ec144f1b2A94", // OIL (SCAM)
    "0x10D7c8107aC7DE97A3BB33c0c215b4Bf45cF405a", // Milk (SCAM)
    "0x5A6D29a2Ab20C606107C4259cd44e2ab1051fcB3", // Trump (SCAM)
    "0x5B16893C38b517d24b3Ed2fA93313fA595867C95", // MOJO (Deprecated)
    "0x7530f1fFA6FBeC35C1f3b2f40f3243a033c99fF3", // DCENT (Deprecated)
    "0x3d80D29Ba41aac5E70C4c4C6a279C92175F5fC33", // BSPUNK (RUG)
    "0xD650E58b6fB323137aDf80eB119Ee3c4093E0823", // THE BEAR (RUGGED)
    "0x566BAEd02b9D7C9C04DFf6Be0bc74bc256a83EdF", // BASE Scam
    "0xF45B55c165199296B8B3E948ba54476864f0Be91", // BASE Scam
    "0x7e90Dd69Ebe30BDCcea04b5d5bb546540223eb92", // BASE Scam
    "0x3135287994299F2E2bd71E6f0DE1164d09b08B8f", // BASE Scam
    "0x2C7236a764B66aEa605a1C693521EDaD0a5DE834", // BASE Scam NFT
    "0xD1533a4FE35D945280729485AE6ed7938A167e3b", // Cherry Blossoms Deprecated
    "0x8c758b95C62D7B6b78919DA5f520Bdef99547653", // Test NFT Contract
    "0xDe74799371Ceac11A0F52BA2694392A391D0dA18", // Old D3 Contract
    "0x2db53e56Cb121bc622F42154c434a2b28A79af66", // Old SIYC Contract
    "0xacC0cec478F8f8FF71494997a756B5D68892cd96", // LONE NFT
    "0x051a112e5b7d56d3825d9720d73ce147c6f08eb8", // Rugged NFT Shibarium
    
  ],
};

const TOKEN_TYPES = {
  ERC20: "ERC-20",
  ERC721: "ERC-721",
  ERC1155: "ERC-1155",
};

const filterTokensByType = (tokens: TokenBalance[], type: string) => {
  return tokens.filter((token) => token.token.type === type);
};

export const filterNfts = (tokens: TokenBalance[]): NftTypeObject => {
  return {
    erc721: tokens.filter((token) => token.token.type === TOKEN_TYPES.ERC721),
    erc1155: tokens.filter((token) => token.token.type === TOKEN_TYPES.ERC1155),
  } as NftTypeObject;
};

export const filterBlacklistedTokens = (
  tokens: TokenBalance[]
): TokenBalance[] => {
  return tokens.filter((token) => {
    const nameMatches = blacklistedTokens.names.some((pattern) =>
      createRegexFromWildcard(pattern).test(token.token.name)
    );
    const symbolMatches = blacklistedTokens.symbols.some((pattern) =>
      createRegexFromWildcard(pattern).test(token.token.symbol)
    );
    const addressMatches = blacklistedTokens.addresses.some((pattern) =>
      token.token.address ? createRegexFromWildcard(pattern).test(token.token.address) : false
    );

    return !nameMatches && !symbolMatches && !addressMatches;
  });
};
